import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Plus, Loader2, Tag as TagIcon } from 'lucide-react';
import client from '../api/client';
import { TAG_COLORS, tagChipClass } from '../lib/tagColors';

// "+" chip on a client/user card: toggle existing tags or create one inline.
// The parent owns the tag list and refetches after changes.
export default function ClientTagAssignPopover({ subjectId, assigned = [], tags = [], onChanged, onTagCreated }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('slate');
  const [saving, setSaving] = useState(false);
  // Local mirror of the assignment. The PUT replaces the whole set, and `assigned`
  // only refreshes after the parent's refetch lands — so toggling two tags quickly
  // off the prop would send a stale set and silently drop the first one.
  const [selected, setSelected] = useState(assigned);
  const assignedKey = assigned.join(',');
  useEffect(() => { setSelected(assigned); }, [assignedKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // Serialize writes so two quick toggles can't race each other server-side.
  const queue = useRef(Promise.resolve());

  const setTags = (tagIds) => {
    queue.current = queue.current
      .then(() => client.put(`/client-tags/subject/${subjectId}`, { tag_ids: tagIds }))
      .then(() => onChanged(subjectId, tagIds))
      .catch(() => toast.error(t('clientTags.updateFailed', 'Could not update tags')));
    return queue.current;
  };

  const toggle = (tagId) => {
    const next = selected.includes(tagId) ? selected.filter(id => id !== tagId) : [...selected, tagId];
    setSelected(next);   // optimistic — the checkmark moves immediately
    setTags(next);
  };

  const createAndAssign = async () => {
    const name = newName.trim();
    if (!name) return;
    if (tags.some(tg => tg.name.toLowerCase() === name.toLowerCase())) {
      toast.error(t('clientTags.alreadyExists', 'A tag with that name already exists'));
      return;
    }
    setSaving(true);
    try {
      const res = await client.post('/client-tags', { name, color: newColor });
      onTagCreated(res.data);
      const next = [...selected, res.data.id];
      setSelected(next);
      await setTags(next);
      setNewName('');
    } catch {
      toast.error(t('clientTags.createFailed', 'Could not create tag'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={e => e.stopPropagation()}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground hover:bg-muted/80 border border-dashed border-border"
          aria-label={t('clientTags.addTag', 'Add tag')}
        >
          <Plus className="h-3 w-3" />
        </button>
      </PopoverTrigger>
      {/* stopPropagation: these cards are clickable and would otherwise navigate */}
      <PopoverContent className="w-60 p-2" align="start" onClick={e => e.stopPropagation()}>
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2 px-1 flex items-center gap-1">
          <TagIcon className="h-3 w-3" /> {t('clientTags.title', 'Tags')}
        </p>
        {tags.length === 0 && (
          <p className="text-xs text-muted-foreground px-1 mb-2">{t('clientTags.empty', 'No tags yet')}</p>
        )}
        {tags.map(tg => (
          <button
            key={tg.id}
            type="button"
            onClick={() => toggle(tg.id)}
            className="w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm hover:bg-muted/50"
          >
            <span className={`h-3.5 w-3.5 rounded-sm border flex items-center justify-center shrink-0 ${selected.includes(tg.id) ? 'bg-primary border-primary' : 'border-border'}`}>
              {selected.includes(tg.id) && <span className="text-white text-[9px]">&#10003;</span>}
            </span>
            <span className={`px-1.5 rounded-full border text-xs ${tagChipClass(tg.color)}`}>{tg.name}</span>
          </button>
        ))}
        <div className="mt-2 pt-2 border-t space-y-1.5">
          <div className="flex gap-1 flex-wrap px-1">
            {TAG_COLORS.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setNewColor(c)}
                aria-label={c}
                className={`h-5 w-5 rounded-full border ${tagChipClass(c)} ${newColor === c ? 'ring-2 ring-primary' : ''}`}
              />
            ))}
          </div>
          <div className="flex gap-1">
            <Input
              value={newName}
              onChange={e => setNewName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createAndAssign()}
              placeholder={t('clientTags.newPlaceholder', 'New tag...')}
              className="h-8 text-xs flex-1"
            />
            <Button size="sm" className="h-8 px-2 bg-primary hover:bg-[#e05200]" onClick={createAndAssign} disabled={saving}>
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
